import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ExternalLink, AlertTriangle, Plus, X, Sparkles } from "lucide-react";
import { getNote, recodeAndWait } from "@/lib/noteClaim";
import { patchNoteSoap } from "@/lib/dictation";
import { provOf, sourceLabel } from "@/lib/createClaimsModel";
import { patientOf } from "@/lib/claimReadiness";
import { useChartTabs } from "@/lib/chartTabs";
import { ChartInsertPanel } from "@/components/dictation/ChartInsert";
import { reformatNote, stripObjObj } from "@/lib/chartInsert";

const INK = "#141412", INDIGO = "#5856D6", SUB = "#6C6C69", FAINT = "#9B9B97", HAIR = "#E9E9EC", PANEL = "#FBFBFA", TINT = "#F1F1FB";
const RED = "#C4302A", REDBG = "#FDECEC", REDBD = "#F3B9B5";
const fmtDos = (iso) => { if (!iso) return "—"; const d = new Date(typeof iso === "string" && iso.length === 10 ? `${iso}T00:00:00` : iso); return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); };

export default function NoteEditor({ noteId, chartUuid, fromLabel = "Encounter review", onClose, onEditing }) {
  const [note, setNote] = useState(null);
  const { openChartTab } = useChartTabs();
  const editInChart = () => openChartTab && openChartTab(chartUuid, { note_id: noteId, edit: true }, note ? patientOf(note) : undefined);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("custom");
  const [drafts, setDrafts] = useState({ custom: "", soap: "" });
  const orig = useRef({ custom: "", soap: "" });
  const hasCustom = !!orig.current.custom.trim() && orig.current.custom.trim() !== orig.current.soap.trim();
  const [saving, setSaving] = useState(false);
  const [recoding, setRecoding] = useState(false);
  const [merging, setMerging] = useState(false);
  const [cancelArmed, setCancelArmed] = useState(false);
  const [staged, setStaged] = useState(() => new Map()); // key -> chart item
  const [pending, setPending] = useState([]);            // items added, awaiting Lumina rebuild on save

  useEffect(() => {
    (async () => {
      try {
        const n = await getNote(noteId); setNote(n);
        const rawC = n?.encounter?.formatted_note || ""; const rawS = n?.encounter?.soap_text || "";
        const c = stripObjObj(rawC), s = stripObjObj(rawS);
        // Keep orig as the raw text so a cleaned "[object Object]" line flags as a change and Save persists it.
        setDrafts({ custom: c, soap: s }); orig.current = { custom: rawC, soap: rawS };
        if (c !== rawC || s !== rawS) toast("Removed a stray \u201c[object Object]\u201d line \u2014 Save to keep the note clean.");
        if (!(c.trim() && c.trim() !== s.trim())) setView("soap");
      } catch { toast.error("Couldn't load the note."); } finally { setLoading(false); }
    })();
  }, [noteId]);

  const dirty = { custom: drafts.custom !== orig.current.custom, soap: drafts.soap !== orig.current.soap };
  const anyDirty = dirty.custom || dirty.soap;
  const hasUnsaved = anyDirty || pending.length > 0 || staged.size > 0;
  // Tells the app shell to ask before the tab's × closes unsaved work.
  useEffect(() => { onEditing?.(hasUnsaved); }, [hasUnsaved]); // eslint-disable-line react-hooks/exhaustive-deps
  const text = drafts[view];
  const setText = (v) => { setDrafts((d) => ({ ...d, [view]: v })); setCancelArmed(false); };

  // ---- stage / add chart items (Lumina) ----
  const toggleStage = (it) => setStaged((cur) => { const m = new Map(cur); m.has(it.key) ? m.delete(it.key) : m.set(it.key, it); return m; });
  const toggleMany = (items, add) => setStaged((cur) => { const m = new Map(cur); items.forEach((it) => (add ? m.set(it.key, it) : m.delete(it.key))); return m; });
  const addToNote = () => {
    if (!staged.size) return;
    const n = staged.size;
    setPending((cur) => { const seen = new Set(cur.map((x) => x.key)); return [...cur, ...[...staged.values()].filter((it) => !seen.has(it.key))]; });
    setStaged(new Map()); setCancelArmed(false);
    toast.success(`Added ${n} item${n === 1 ? "" : "s"} — not saved yet`);
  };
  const removePending = (key) => setPending((cur) => cur.filter((x) => x.key !== key));

  const doSave = async (recode) => {
    setSaving(true);
    try {
      let useDrafts = drafts;
      // Fold staged chart items in via Lumina AI, rebuilding the active note version.
      if (pending.length) {
        setMerging(true);
        const additions = pending.map((it) => `[${it.cat}] ${it.text}${it.summary ? `\n${it.summary}` : ""}`);
        const meta = { sessionId: note?.encounter?.dictation_session_id, clientId: note?.client_id, npi: note?.npi_number, specialty: note?.taxonomy_desc };
        let rebuilt;
        try { rebuilt = await reformatNote(drafts[view], additions, meta); }
        catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Lumina couldn't rebuild the note — your added items are kept."); setMerging(false); return; }
        setMerging(false);
        if (!rebuilt) { toast.error("Lumina couldn't rebuild the note — your added items are kept."); return; }
        useDrafts = { ...drafts, [view]: rebuilt };
        setDrafts(useDrafts); setPending([]);
      }
      const dc = useDrafts.custom !== orig.current.custom, ds = useDrafts.soap !== orig.current.soap;
      if (!dc && !ds && !recode) { toast("No changes to save"); onClose?.(false, noteId); return; }
      if (dc || ds) {
        const body = {};
        if (dc) body.formatted_note = useDrafts.custom;
        if (ds) body.soap_text = useDrafts.soap;
        try { await patchNoteSoap(noteId, body); } catch (e) { toast.error(e?.message || "Couldn't save the note."); return; }
        orig.current = { custom: useDrafts.custom, soap: useDrafts.soap };
      }
      if (!recode) { toast.success("Saved. Codes are out of date until you recode."); onClose?.(true, noteId); return; }
      setRecoding(true);
      let r;
      try { r = await recodeAndWait(noteId, { timeoutMs: 180000, onNote: (n) => { if (n?.recode_status === "running") setRecoding("running"); } }); }
      catch (e) { toast.error(e?.response?.data?.detail || "Saved, but the recode didn't start. Use Recode on the row."); onClose?.(true, noteId); return; }
      if (r.already) toast("A recode was already running. revQ waited for it to finish.");
      if (r.status === "done") toast.success("Saved and recoded from the note.");
      else if (r.status === "failed") toast.error(`Saved, but the recode failed: ${r.note?.recode_error || "no reason given"}. The previous codes are kept.`);
      else if (r.status === "busy") toast("Saved. The visit is still being processed, so it can't be recoded yet. Use Recode on the row once it's ready.");
      else toast("Saved. The recode is still running\u2026 the row updates when the new codes are in.");
      onClose?.(true, noteId);
    } finally { setSaving(false); setRecoding(false); setMerging(false); }
  };

  const cancel = () => {
    if (!hasUnsaved) { onClose?.(false, noteId); return; }
    if (!cancelArmed) { setCancelArmed(true); toast(anyDirty ? "You have unsaved changes. Click Cancel again to discard them." : "Added chart items aren't saved yet. Click Cancel again to discard them."); return; }
    onClose?.(false, noteId);
  };

  const busy = saving || recoding || merging;
  const saveLabel = merging ? "Adding with Lumina\u2026" : "Save & recode";

  if (loading) return <div className="py-16 text-center text-[13px]" style={{ color: FAINT }}><Loader2 size={16} className="animate-spin inline mr-2" /> Loading note…</div>;

  return (
    <div className="px-6 py-4" data-testid={`note-editor-${noteId}`}>
      <button onClick={cancel} className="text-[12px] font-semibold inline-flex items-center gap-1 mb-2" style={{ color: INDIGO }} data-testid="ne-back"><ChevronLeft size={13} /> {fromLabel}</button>

      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h1 className="text-[22px] font-extrabold tracking-[-0.4px]" style={{ color: INK }}>Edit visit note</h1>
          <div className="text-[12px] mt-0.5" style={{ color: FAINT }}>{note ? `${patientOf(note)} · ${fmtDos(note.date_of_service)} · ${sourceLabel(note)} · ${provOf(note) || "—"}` : ""}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {chartUuid ? <button onClick={editInChart} className="text-[12px] font-semibold inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px]" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid="ne-edit-full-chart"><ExternalLink size={13} /> Edit in full chart</button> : null}
          <button onClick={cancel} className="text-[12px] font-semibold px-3 py-1.5 rounded-[8px]" style={{ border: `1px solid ${cancelArmed ? RED : HAIR}`, color: cancelArmed ? RED : INK }} data-testid="ne-cancel">{cancelArmed ? (anyDirty ? "Discard changes?" : "Discard added items?") : "Cancel"}</button>
          <button onClick={() => doSave(false)} disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-[8px] disabled:opacity-50 inline-flex items-center gap-1.5" style={{ border: `1px solid ${HAIR}`, color: INK }} data-testid="ne-save">{merging ? <Loader2 size={13} className="animate-spin" /> : null} Save</button>
          <button onClick={() => doSave(true)} disabled={busy} className="text-[12px] font-semibold px-4 py-1.5 rounded-[8px] inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: INK, color: "#fff" }} data-testid="ne-save-recode">{(recoding || merging) ? <Loader2 size={13} className="animate-spin" /> : null} {saveLabel}</button>
        </div>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: "minmax(0,1fr) 360px" }}>
        {/* left · note */}
        <div className="min-w-0">
          {!hasCustom ? (
            <div className="mb-1.5 text-[12px]" data-testid="ne-view-standard-only"><b className="font-semibold" style={{ color: INK }}>Standard SOAP</b> <span style={{ color: FAINT }}>No custom format for this provider. Coding reads this version.</span></div>
          ) : <div className="flex items-center gap-2 mb-1.5">
            <div className="inline-flex items-center rounded-[8px] p-[2px]" style={{ background: "#F1F1F0" }}>
              {[["custom", "Custom note"], ["soap", "Standard SOAP"]].map(([k, t]) => (
                <button key={k} onClick={() => setView(k)} className="text-[11.5px] font-semibold px-2.5 py-1 rounded-[6px]" style={{ background: view === k ? "#fff" : "transparent", color: view === k ? INK : SUB, boxShadow: view === k ? "0 1px 2px rgba(0,0,0,.1)" : "none" }} data-testid={`ne-view-${k}`}>{t}</button>
              ))}
            </div>
            <span className="text-[11px]" style={{ color: FAINT }}>{view === "custom" ? "Coding reads this version." : "Coding reads the custom note. Edits here save to Standard SOAP only."}</span>
          </div>}
          <div className="text-[11px] mb-1.5" style={{ color: FAINT }}>Saving marks the codes out of date until they're recoded from the note.</div>

          {pending.length ? (
            <div className="rounded-[8px] px-3 py-2.5 mb-2" style={{ background: REDBG, border: `1px solid ${REDBD}` }} data-testid="ne-notsaved">
              <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                <AlertTriangle size={13} style={{ color: RED }} />
                <span className="text-[12px] font-bold" style={{ color: RED }}>{pending.length} chart item{pending.length === 1 ? "" : "s"} added — NOT SAVED</span>
                <span className="text-[11.5px]" style={{ color: "#A63B36" }}>Click Save or Save &amp; recode to keep {pending.length === 1 ? "it" : "them"}.</span>
                <button onClick={() => setPending([])} className="ml-auto text-[11px] font-semibold" style={{ color: RED }} data-testid="ne-notsaved-clear">Clear</button>
              </div>
              <div className="flex flex-wrap gap-1">
                {pending.map((p) => (
                  <span key={p.key} className="inline-flex items-center gap-1 text-[10.5px] rounded-[5px] px-1.5 py-0.5" style={{ background: "#fff", border: `1px solid ${REDBD}`, color: INK }} data-testid={`ne-pending-${p.key}`}>
                    <span className="opacity-60">{p.cat}</span><span className="max-w-[200px] truncate">{p.name || p.text}</span>
                    <button onClick={() => removePending(p.key)} aria-label="Remove"><X size={11} /></button>
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck
            className="w-full rounded-[10px] p-3.5 outline-none resize-y" style={{ minHeight: 560, background: PANEL, border: `1px solid ${HAIR}`, fontSize: 13.5, lineHeight: 1.6, color: INK }} data-testid="ne-textarea" />
        </div>

        {/* right · pull from chart (Lumina) */}
        <div className="min-w-0">
          {!chartUuid ? (
            <div className="sticky top-2 rounded-[11px] px-3.5 py-6 text-[12px]" style={{ border: `1px solid ${HAIR}`, background: "#fff", color: FAINT }} data-testid="ne-nochart">This visit isn't linked to a chart, so there's nothing to pull in.</div>
          ) : (
            <div className="sticky top-2 flex flex-col gap-2" style={{ maxHeight: "calc(100vh - 110px)" }}>
              <div className="rounded-[11px] px-3 py-2 flex-none" style={{ border: `1px solid ${HAIR}`, background: staged.size ? TINT : "#fff" }} data-testid="ne-tray">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[.05em]" style={{ color: INK }}>Staged</span>
                  <span className="text-[10px] font-bold tabular-nums" style={{ color: FAINT }}>{staged.size}</span>
                  {staged.size ? <button onClick={() => setStaged(new Map())} className="text-[10.5px] font-semibold" style={{ color: SUB }} data-testid="ne-tray-clear">Clear</button> : null}
                  <button onClick={addToNote} disabled={!staged.size} className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-[7px] disabled:opacity-40" style={{ background: staged.size ? INK : "#fff", color: staged.size ? "#fff" : SUB, border: staged.size ? "none" : `1px solid ${HAIR}` }} data-testid="ne-add-to-note"><Plus size={12} /> Add {staged.size || ""} to note</button>
                </div>
                {staged.size ? (
                  <div className="flex flex-wrap gap-1 mt-1.5 max-h-[60px] overflow-y-auto">
                    {[...staged.values()].map((it) => (
                      <span key={it.key} className="inline-flex items-center gap-1 text-[10.5px] rounded-[5px] px-1.5 py-0.5" style={{ background: "#fff", border: `1px solid ${HAIR}`, color: INK }}><span className="max-w-[150px] truncate">{it.name || it.text}</span><button onClick={() => toggleStage(it)} aria-label="Unstage"><X size={10} /></button></span>
                    ))}
                  </div>
                ) : <div className="text-[11px] mt-1 inline-flex items-center gap-1" style={{ color: FAINT }}><Sparkles size={11} style={{ color: INDIGO }} /> Check items below, then add — Lumina folds them in on save.</div>}
              </div>
              <ChartInsertPanel note={note} variant="card" stagedKeys={new Set(staged.keys())} onToggleItem={toggleStage} onToggleMany={toggleMany} noteText={drafts[view]} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
